import React, {useState} from 'react';
import {View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {noop} from 'lodash';

import Avatar from '/component/base/Avatar';
import {Banner} from '/component/base/Banner/Banner';
import Button from '/component/base/Button';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {DownlineProfile} from '/constant/TestIDs';
import sharedTestIDs from '/constant/TestIDs/Shared';
import {useTranslation} from '/provider/TranslationProvider';
import {safeLayoutAnimation} from '/util/animation.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './ProfileAlreadyExistsBanner.style';
import {generateOpenDeletePopover} from './ProfileAlreadyExistsBanner.util';

interface ClientDetails {
  firstName: string;
  lastName: string;
  avatarUrl: string;
  emailAddress: string;
  popoverShow: () => void;
  candidateId: string;
  deleteCandidate: (params: {candidateId: string}) => Promise<void>;
}

interface Props {
  clientDetails: ClientDetails | null;
  onMergePress: () => void;
}

export const ProfileAlreadyExistsBanner: React.FC<Props> = ({
  clientDetails,
  onMergePress = noop,
}) => {
  const {
    firstName,
    lastName,
    avatarUrl,
    emailAddress,
    popoverShow,
    candidateId,
    deleteCandidate,
  } = clientDetails || {};

  const {ts} = useTranslation('ProfileAlreadyExistsBanner');
  const navigation = useNavigation();
  const [expanded, setExpanded] = useState(true);

  const handleDeleteProfile = async () => {
    if (candidateId && deleteCandidate) {
      await deleteCandidate({candidateId});
      navigation.goBack();
    }
  };

  const toggleExpand = () => {
    safeLayoutAnimation();
    setExpanded(!expanded);
  };

  return (
    <View
      style={styles.container}
      {...testProperties(DownlineProfile.mergeBannerRoot)}>
      <Banner style={{base: styles.base}} verticalBanner alertLevel="info">
        <TouchableOpacity
          style={styles.accordionHeader}
          onPress={toggleExpand}
          {...testProperties(null, true)}>
          <Icon
            name="alert"
            style={styles.alertButtonIcon}
            {...testProperties(DownlineProfile.mergeBannerIcon)}
          />
          <Text {...testProperties(DownlineProfile.mergeBannerHeader)} isBold>
            {ts('Accordion Title', 'Existing Client in your organization')}
          </Text>
          <Icon
            name={expanded ? 'chevron-up' : 'chevron-down'}
            style={styles.accordionIcon}
            {...testProperties(DownlineProfile.mergeBannerArrowButton)}
          />
        </TouchableOpacity>

        <View
          style={{
            height: expanded ? undefined : 0,
            overflow: 'hidden',
          }}>
          {expanded && clientDetails && (
            // @ts-expect-error TS(2339): Property 'accordionBody' does not exist on type 'N... Remove this comment to see the full error message
            <View style={styles.accordionBody}>
              <View style={styles.accordionBodyContent}>
                <Avatar
                  size={64}
                  avatarUrl={avatarUrl}
                  avatarKey={emailAddress}
                />
                <View style={styles.textContainer}>
                  <Text
                    style={styles.textNameLabel}
                    {...testProperties(DownlineProfile.mergeBannerName)}
                    isBold>
                    {firstName} {lastName}
                  </Text>
                  <Text
                    style={styles.textEmail}
                    {...testProperties(DownlineProfile.mergeBannerEmail)}>
                    {emailAddress}
                  </Text>
                </View>
              </View>

              <Button
                onPress={onMergePress}
                accessibilityLabel={ts(
                  'merge button',
                  'Move Candidate info to Client',
                )}
                style={{base: styles.buttonBox}}
                {...testProperties(
                  DownlineProfile.mergeBannerMoveToClientButton,
                )}>
                {ts('merge button', 'Move Candidate info to Client')}
              </Button>

              <Button
                type="textLink"
                accessibilityLabel={ts(
                  'delete button',
                  'Archive candidate profile',
                )}
                onPress={generateOpenDeletePopover(
                  popoverShow,
                  handleDeleteProfile,
                )}
                {...testProperties(DownlineProfile.mergeBannerArchive)}>
                <View
                  style={styles.textStyle}
                  {...testProperties(sharedTestIDs.view)}>
                  <Icon
                    name="trash"
                    style={styles.trashIcon}
                    {...testProperties(DownlineProfile.mergeBannerArchiveIcon)}
                  />
                  <Text style={styles.removeLabel} isBold>
                    {ts('delete button', 'Archive candidate profile')}
                  </Text>
                </View>
              </Button>
            </View>
          )}
        </View>
      </Banner>
    </View>
  );
};

export default ProfileAlreadyExistsBanner;
